import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useTranslation } from 'react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import i18n from '@src/languages';
import { Button, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { PACK_DURATION } from '@src/constants/package';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';

const formatNumber = (num, language = 'en') => {
  const suffixes = {
    en: {
      K: 'K',
      M: 'M',
      B: 'B',
    },
    vi: {
      K: 'K',
      M: ' triệu',
      B: ' tỷ',
    },
  };

  const langSuffixes = suffixes[language] || suffixes.en;
  const format = (value, suffix) =>
    `${value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}${suffix}`;

  if (num >= 1e9) return format(num / 1e9, langSuffixes.B);
  if (num >= 1e6) return format(num / 1e6, langSuffixes.M);
  if (num >= 1e3) return format(num / 1e3, langSuffixes.K);
  return num.toString();
};

const FreeAmountCredits = ({
  packageCode,
  introductoryPeriodDays,
  regularDailyCredits,
  introductoryDailyCredits,
}) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const [openPopup, setOpenPopup] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClickShowPopup = () => {
    if (isMobile) setOpenPopup(true);
    return null;
  };

  return (
    <div className="free-amount-credits">
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(
            t('introductoryDailyCredits', {
              introductoryDailyCredits:
                introductoryDailyCredits?.toLocaleString(language),
            }),
          ),
        }}
      />
      <Tooltip
        arrow
        data-id="tooltip-plan-compare"
        data-package-code={packageCode}
        title={
          <Typography
            component="span"
            sx={{ fontSize: '14px' }}
            style={{ textAlign: 'left' }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('introductoryPeriodDays', {
                  introductoryPeriodDays,
                  regularDailyCredits:
                    regularDailyCredits?.toLocaleString(language),
                  introductoryDailyCredits:
                    introductoryDailyCredits?.toLocaleString(language),
                }),
              ),
            }}
          />
        }
        placement="top"
      >
        <InfoRoundedIcon
          onClick={handleClickShowPopup}
          className="info-out-line-icon"
        />
      </Tooltip>
      <NotificationDialogV3
        name={t('detailedInformation')}
        title={t('detailedInformation')}
        description={t('introductoryPeriodDays', {
          introductoryPeriodDays,
          regularDailyCredits: regularDailyCredits?.toLocaleString(language),
          introductoryDailyCredits:
            introductoryDailyCredits?.toLocaleString(language),
        })}
        open={openPopup && isMobile}
        onClose={() => setOpenPopup(false)}
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopup(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </div>
  );
};

const PurchaseAmountCredits = ({ creditsPerCycle, totalCredits, duration }) => {
  const { t } = useTranslation();
  return (
    <div>
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(
            t('amountCreditsPerCycle', {
              creditsPerCycle,
            }),
          ),
        }}
      />

      {totalCredits && (
        <span>
          {' '}
          (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('totalCredits', {
                  totalCredits,
                  duration,
                }),
              ),
            }}
          />
          )
        </span>
      )}
    </div>
  );
};

const AmountCredits = ({ pkg }) => {
  // Free package
  const { language } = i18n;
  const hasFreemiumConfig = pkg?.freemiumConfig;
  if (hasFreemiumConfig) {
    return (
      <FreeAmountCredits
        packageCode={pkg?.code}
        introductoryPeriodDays={pkg?.freemiumConfig?.introductoryPeriodDays}
        introductoryDailyCredits={pkg?.freemiumConfig?.introductoryDailyCredits}
        regularDailyCredits={pkg?.freemiumConfig?.regularDailyCredits}
      />
    );
  }

  // Purchase package
  const { creditsPerCycle, totalCycles } = pkg?.cycle || {};
  const totalCredits =
    pkg?.expiresIn !== PACK_DURATION.MONTH ? creditsPerCycle * totalCycles : 0;

  return (
    <PurchaseAmountCredits
      creditsPerCycle={
        creditsPerCycle ? formatNumber(creditsPerCycle, language) : null
      }
      totalCredits={totalCredits ? formatNumber(totalCredits, language) : null}
    />
  );
};

export default AmountCredits;
